import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../hooks/useSocket';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, FolderKanban, ClipboardCheck, Calendar, ShoppingCart,
  MessageSquare, Users, Package, Menu, X, Bell, LogOut, ChevronRight,
  Store, Hammer, Receipt, IndianRupee, Wrench, FileText, Truck, ClipboardList
} from 'lucide-react';

const allNav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'md', 'manager', 'supervisor', 'accounts', 'purchase'], bottom: true },
  { to: '/projects', icon: FolderKanban, label: 'Projects', roles: ['admin', 'md', 'manager', 'supervisor', 'accounts', 'purchase'], bottom: true },
  { to: '/attendance', icon: ClipboardCheck, label: 'Attendance', roles: ['admin', 'md', 'manager', 'supervisor'] },
  { to: '/leave', icon: Calendar, label: 'Leave', roles: ['admin', 'md', 'manager', 'supervisor'] },
  { to: '/purchase-orders', icon: ShoppingCart, label: 'Purchase', roles: ['admin', 'md', 'purchase', 'manager', 'supervisor'] },
  { to: '/expenses', icon: Receipt, label: 'Expenses', roles: ['admin', 'md', 'manager', 'supervisor', 'accounts', 'hr'] },
  { to: '/work-orders', icon: Hammer, label: 'Project Work Orders', roles: ['admin', 'md', 'manager', 'supervisor', 'accounts'] },
  { to: '/factory-work-orders', icon: Wrench, label: 'Factory Work Orders', roles: ['admin', 'md', 'manager', 'supervisor', 'accounts', 'factory'] },
  { to: '/transport-docs', icon: Truck, label: 'Transport & Bills', roles: ['admin', 'md', 'accounts', 'purchase', 'factory', 'manager'] }, // EXP21
  { to: '/wo-tracker', icon: ClipboardList, label: 'WO Tracker', roles: ['admin', 'md', 'accounts', 'purchase', 'manager', 'supervisor'] }, // EXP31
  { to: '/vendors', icon: Store, label: 'Vendors', roles: ['admin', 'md', 'manager', 'supervisor', 'accounts', 'purchase'] },
  { to: '/accounts', icon: IndianRupee, label: 'Accounts', roles: ['admin', 'md', 'accounts'] },
  { to: '/products', icon: Package, label: 'Products', roles: ['admin', 'md', 'accounts'] }, // AVB3-nav
  { to: '/supervisor-ledger', icon: IndianRupee, label: 'Supervisor Ledger', roles: ['admin', 'md', 'hr'] },
  { to: '/factory', icon: Package, label: 'Factory', roles: ['admin', 'md', 'purchase', 'factory', 'supervisor'], bottom: true },
  { to: '/admin', icon: Users, label: 'Admin', roles: ['admin', 'md', 'hr'], bottom: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [gcUnread, setGcUnread] = useState(0);
  const [projectUnread, setProjectUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [attendancePending, setAttendancePending] = useState(false);
  const [purchasePending, setPurchasePending] = useState(0);
  const [projectUnreads, setProjectUnreads] = useState(0);
  const [workOrderPending, setWorkOrderPending] = useState(0);
  const [projectWoPending, setProjectWoPending] = useState(0);
  const [expensePending, setExpensePending] = useState(0);
  const [tdocsOk, setTdocsOk] = useState(false); // EXP25 Transport access given per user
  useEffect(() => {
    if (!user || ['admin', 'md', 'accounts', 'purchase', 'factory', 'manager'].includes(user.role)) return;
    api.get('/tdocs/me').then((r) => setTdocsOk(!!(r.data && r.data.ok))).catch(() => {});
  }, [user]);

  const { onNotification, onGroupMessage } = useSocket(user?.id);

  useEffect(() => {
    if (!onNotification) return;
    const unsub = onNotification((notif) => {
      setUnreadCount(c => c + 1);
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      toast(notif.title + ': ' + (notif.message || '').substring(0, 60), { icon: '🔔', duration: 5000 });
    });
    return unsub;
  }, [onNotification]);

  useEffect(() => {
    if (!onGroupMessage) return;
    const unsub = onGroupMessage((msg) => {
      if (msg.sender?.id === user?.id) return;
      if (!location.pathname.includes('/group-chat')) {
        setGcUnread(c => c + 1);
      }
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      toast(`${msg.sender?.name || 'Team'}: ${(msg.content_text || '').substring(0, 60)}`, { icon: '💬', duration: 4000 });
      if (document.hidden && Notification.permission === 'granted') {
        try { new Notification(`${msg.sender?.name} in InteriorPoint Group Chat`, { body: msg.content_text?.substring(0, 100), icon: '/logo192.png' }); } catch (e) {}
      }
    });
    return unsub;
  }, [onGroupMessage, user?.id, location.pathname]);

  useEffect(() => {
    const f = () => {
      api.get('/notifications/unread-count').then(r => setUnreadCount(r.data.count)).catch(() => {});
      api.get('/notifications/unread-summary').then(r => setProjectUnread(r.data.totalProjects || 0)).catch(() => {});
      if (!location.pathname.includes('/group-chat')) {
        api.get('/group-chat/unread').then(r => setGcUnread(r.data.unread)).catch(() => {});
      }
    };
    f(); const i = setInterval(f, 10000); return () => clearInterval(i);
  }, [location.pathname]);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/group-chat')) {
      setGcUnread(0);
      api.post('/group-chat/mark-read').catch(() => {});
    }
    if (notifications.length > 0) {
      let toMark = [];
      if (path.includes('/projects/')) {
        const projectId = path.split('/projects/')[1]?.split('/')[0];
        toMark = notifications.filter(n => !n.is_read && n.data?.project_id === projectId).map(n => n.id);
      } else if (path.includes('/purchase-orders')) {
        toMark = notifications.filter(n => !n.is_read && n.entity_type === 'purchase_order').map(n => n.id);
      } else if (path.includes('/leave')) {
        toMark = notifications.filter(n => !n.is_read && n.entity_type === 'leave_request').map(n => n.id);
      } else if (path.includes('/attendance')) {
        toMark = notifications.filter(n => !n.is_read && n.entity_type === 'attendance').map(n => n.id);
      }
      if (toMark.length > 0) {
        api.put('/notifications/read', { ids: toMark }).then(() => {
          setUnreadCount(c => Math.max(0, c - toMark.length));
          setNotifications(ns => ns.map(n => toMark.includes(n.id) ? { ...n, is_read: true } : n));
        }).catch(() => {});
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const total = (unreadCount || 0) + (gcUnread || 0) + (projectUnreads || 0);
    try {
      if (navigator.setAppBadge) {
        if (total > 0) navigator.setAppBadge(total);
        else navigator.clearAppBadge();
      }
    } catch (e) {}
  }, [unreadCount, gcUnread]);

  useEffect(() => {
    const check = () => {
      const todayStr = new Date().toISOString().split('T')[0];
      api.get(`/attendance/my?from=${todayStr}&to=${todayStr}`).then(r => {
        setAttendancePending(!(r.data.attendance || []).some(a => a.check_in_at));
      }).catch(() => {});

      if (['admin', 'md', 'purchase', 'factory', 'supervisor'].includes(user?.role)) {
        api.get('/purchase-orders').then(r => {
          const c = r.data.counts || {};
          setPurchasePending((c.pending_purchase_mgr || 0) + (c.pending_md || 0));
        }).catch(() => {});
      }

      if (['admin', 'md', 'manager', 'supervisor', 'accounts'].includes(user?.role)) {
        api.get('/factory/work-orders/all-requests').then(r => {
          const c = r.data.counts || {};
          setWorkOrderPending((c.pending_admin || 0) + (c.pending_md || 0));
        }).catch(() => {});

        api.get('/work-orders/pending-approvals').then(r => {
          setProjectWoPending((r.data.workOrders || []).length);
        }).catch(() => {});

        api.get('/expense-requests').then(r => {
          const c = r.data.counts || {};
          setExpensePending((c.pending_admin || 0) + (c.pending_md || 0));
        }).catch(() => {});
      }

      api.get('/projects/unread-counts').then(r => {
        const unreads = r.data.unreads || {};
        const total = Object.values(unreads).reduce((s, u) => s + (u.total || 0), 0);
        setProjectUnreads(total);
      }).catch(() => {});
    };
    check(); const i = setInterval(check, 30000); /* EXP19: also refresh on page change + after approve/reject */
    window.addEventListener('pms-refresh-badges', check);
    return () => { clearInterval(i); window.removeEventListener('pms-refresh-badges', check); };
  }, [user?.role, location.pathname]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    let lastSentTime = 0;
    let watchId = null;
    let flushInterval = null;
    let latestPos = null;

    const sendPing = (lat, lng) => {
      const now = Date.now();
      if (now - lastSentTime < 5 * 60 * 1000) return;
      lastSentTime = now;
      api.post('/attendance/ping-location', { lat, lng }).catch(() => {});
    };

    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          latestPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          if (lastSentTime === 0) sendPing(latestPos.lat, latestPos.lng);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 120000 }
      );
    } catch (e) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          latestPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          sendPing(latestPos.lat, latestPos.lng);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      );
    }

    flushInterval = setInterval(() => {
      if (latestPos) {
        sendPing(latestPos.lat, latestPos.lng);
      } else {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            latestPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            sendPing(latestPos.lat, latestPos.lng);
          },
          () => {},
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 120000 }
        );
      }
    }, 5 * 60 * 1000);

    const handleResume = () => {
      if (latestPos) {
        lastSentTime = 0;
        sendPing(latestPos.lat, latestPos.lng);
      } else {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            lastSentTime = 0;
            sendPing(pos.coords.latitude, pos.coords.longitude);
          },
          () => {},
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
        );
      }
    };
    window.addEventListener('focus', handleResume);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) handleResume(); });

    let wakeLock = null;
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
          wakeLock.addEventListener('release', () => { wakeLock = null; });
        }
      } catch (e) {}
    };
    requestWakeLock();
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !wakeLock) requestWakeLock();
    });

    return () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (flushInterval) clearInterval(flushInterval);
      window.removeEventListener('focus', handleResume);
      if (wakeLock) { try { wakeLock.release(); } catch (e) {} }
    };
  }, []);

  useEffect(() => {
    const subscribePush = async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
        const reg = await navigator.serviceWorker.ready;
        const { data } = await api.get('/notifications/vapid-key');
        if (!data.publicKey) return;
        const urlBase64 = data.publicKey;
        const padding = '='.repeat((4 - urlBase64.length % 4) % 4);
        const base64 = (urlBase64 + padding).replace(/-/g, '+').replace(/_/g, '/');
        const raw = window.atob(base64);
        const key = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) key[i] = raw.charCodeAt(i);
        let sub = await reg.pushManager.getSubscription();
        if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
        await api.post('/notifications/push-subscribe', { subscription: sub.toJSON() });
      } catch (e) { console.warn('[Push]', e.message); }
    };
    subscribePush();
  }, []);

  const loadNotifications = async () => {
    try { const { data } = await api.get('/notifications?limit=50'); setNotifications(data.notifications); setShowNotifs(s => !s); } catch {}
  };

  useEffect(() => {
    api.get('/notifications?limit=50').then(r => setNotifications(r.data.notifications || [])).catch(() => {});
  }, []);

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      try { await api.put('/notifications/read', { ids: [notif.id] }); setUnreadCount(c => Math.max(0, c - 1)); } catch {}
    }
    setShowNotifs(false);
    const d = notif.data || {};
    if (d.project_id) navigate(`/projects/${d.project_id}`);
    else if (notif.entity_type === 'purchase_order') navigate('/purchase-orders');
    else if (notif.entity_type === 'leave_request') navigate('/leave');
    else if (notif.entity_type === 'attendance') navigate('/attendance');
    else navigate('/dashboard');
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const filteredNav = allNav.filter(n => n.roles.includes(user?.role) || (n.to === '/transport-docs' && tdocsOk)); // EXP25
  const bottomItems = filteredNav.filter(n => n.bottom).slice(0, 5);

  return (
    <div className="flex flex-col h-screen bg-gray-50 font-sans" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <header className="fixed top-0 w-full z-50 bg-white shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex justify-between items-center px-3 h-12">
          <div className="flex items-center gap-2">
            <button onClick={() => setDrawerOpen(true)} className="p-2 hover:bg-gray-100 rounded-lg active:scale-95">
              <Menu size={20} className="text-orange-700" />
            </button>
            <h1 className="text-base font-black text-orange-700 tracking-tighter uppercase">Interior Point</h1>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={loadNotifications} className="relative p-2 hover:bg-gray-100 rounded-lg">
              <Bell size={18} className="text-gray-600" />
              {unreadCount > 0 && <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>
            <button onClick={() => navigate('/profile')} className="w-8 h-8 rounded-full bg-orange-100 border-2 border-orange-500 flex items-center justify-center text-xs font-black text-orange-700">
              {user?.name?.[0]}
            </button>
          </div>
        </div>
      </header>

      {showNotifs && (
        <>
          <div className="fixed inset-0 z-50" onClick={() => setShowNotifs(false)} />
          <div className="fixed right-2 z-50 bg-white rounded-xl shadow-2xl border overflow-hidden" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 52px)', width: 'calc(100vw - 16px)', maxWidth: '320px', maxHeight: '70vh' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <p className="text-sm font-black uppercase tracking-wide text-gray-700">Notifications</p>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
              {notifications.map(n => (
                <div key={n.id} onClick={() => handleNotifClick(n)}
                  className={`px-4 py-3 border-b cursor-pointer hover:bg-orange-50 transition ${n.is_read ? 'opacity-60' : 'bg-orange-50/50'}`}>
                  <p className="text-xs font-bold text-gray-800">{n.title}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{n.message?.substring(0, 80)}</p>
                </div>
              ))}
              {notifications.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No notifications</p>}
            </div>
          </div>
        </>
      )}

      {drawerOpen && <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setDrawerOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-white transform transition-transform duration-300 shadow-2xl ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center justify-between h-12 px-4 border-b">
          <h2 className="text-base font-black text-orange-700 uppercase tracking-tighter">Interior Point</h2>
          <button onClick={() => setDrawerOpen(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-500" /></button>
        </div>
        <div className="px-4 py-3 border-b heavy-wash text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-black">{user?.name?.[0]}</div>
            <div><p className="font-bold text-sm">{user?.name}</p><p className="text-[10px] text-orange-100 uppercase tracking-wide">{user?.role}</p></div>
          </div>
        </div>
        <nav className="py-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px - env(safe-area-inset-top, 0px))' }}>
          {filteredNav.map(item => (
            <NavLink key={item.to} to={item.to} onClick={() => setDrawerOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 mx-2 rounded-lg text-sm transition-all ${isActive ? 'bg-orange-50 text-orange-700 font-bold hazard-stripe' : 'text-gray-600 hover:bg-gray-50'}`}>
              <item.icon size={18} />
              <span className="flex-1 font-semibold uppercase tracking-wide text-[11px]">{item.label}</span>
              {item.to === '/group-chat' && gcUnread > 0 && (
                <span className="w-5 h-5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{gcUnread > 99 ? '99+' : gcUnread}</span>
              )}
              {item.to === '/projects' && projectUnread > 0 && (
                <span className="w-5 h-5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{projectUnread}</span>
              )}
              {item.to === '/attendance' && attendancePending && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">!</span>
              )}
              {item.to === '/purchase-orders' && purchasePending > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{purchasePending}</span>
              )}
              {item.to === '/work-orders' && projectWoPending > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{projectWoPending}</span>
              )}
              {item.to === '/factory-work-orders' && workOrderPending > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{workOrderPending > 99 ? '99+' : workOrderPending}</span>
              )}
              {item.to === '/expenses' && expensePending > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{expensePending}</span>
              )}
              {item.to === '/projects' && projectUnreads > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{projectUnreads > 99 ? '99+' : projectUnreads}</span>
              )}
              <ChevronRight size={14} className="text-gray-300" />
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t" style={{ paddingBottom: 'env(safe-area-inset-bottom, 12px)' }}>
          <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-bold uppercase tracking-wide">
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto" style={{ marginTop: 'calc(env(safe-area-inset-top, 0px) + 48px)', marginBottom: '64px' }}>
        <div className="max-w-5xl mx-auto px-3 py-3"><Outlet /></div>
      </main>

      <nav className="fixed bottom-0 w-full z-40 bg-white border-t-4 border-orange-500 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 4px)' }}>
        <div className="flex justify-around items-center px-1 pt-1">
          {bottomItems.map(item => (
            <NavLink key={item.to} to={item.to}
              className={({ isActive }) => `flex flex-col items-center justify-center px-1.5 py-1 rounded-lg transition-all min-w-[48px] relative ${isActive ? 'bg-orange-500 text-white' : 'text-gray-500'}`}>
              {({ isActive }) => (
                <>
                  <item.icon size={18} strokeWidth={isActive ? 2.5 : 1.5} />
                  <span className="text-[9px] font-bold uppercase tracking-wide mt-0.5">{item.label}</span>
                  {item.to === '/group-chat' && gcUnread > 0 && !isActive && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">{gcUnread}</span>
                  )}
                  {item.to === '/projects' && projectUnreads > 0 && !isActive && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">{projectUnreads > 99 ? '99+' : projectUnreads}</span>
                  )}
                  {item.to === '/projects' && projectUnread > 0 && !isActive && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">{projectUnread}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
          <button onClick={() => setDrawerOpen(true)} className="flex flex-col items-center justify-center px-1.5 py-1 text-gray-500 min-w-[48px]">
            <Menu size={18} strokeWidth={1.5} />
            <span className="text-[9px] font-bold uppercase tracking-wide mt-0.5">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
}